import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAuthHeaders } from "../../api/authHelper";
import "../Admin/VolunteerGroup/VolunteerGroup.css";
import Billede1 from "../../Images/Billede1.jpg";
import PersonSimpleModalForm from "./PersonSimpleModalForm";
import HeroSectionV2 from "../../components/HeroSectionV2";
import { Person } from "../Admin/ID/types";

interface PersonWithShifts extends Person {
    shifts?: {
        shiftId: number;
        title: string;
        functionName: string;
        start: string;
        end: string;
        status: string;
    }[];
}

interface VolunteerGroup {
    id?: number;
    name: string;
    type: string;
    eventId: number;
    maxSlots: number;
    contactPersonId?: number;
    selfAssignShifts: boolean;
    deadline?: string;
}

interface Shift {
    shiftId: number;
    functionId: number;
    functionName: string;
    title: string;
    start: string;
    end: string;
    maxParticipants?: number;
    registeredCount?: number;
    waitlistAllowed?: boolean;
}

const VolunteerGroupsDashboard: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const { eventId } = useParams();

    const selectedEventId = eventId ? Number(eventId) : null;

    const [groups, setGroups] = useState<VolunteerGroup[]>([]);
    const [loading, setLoading] = useState(true);

    const [events, setEvents] = useState<any[]>([]);
    const [functions, setFunctions] = useState<any[]>([]);

    const [selectedGroupId, setSelectedGroupId] =
        useState<number | null>(null);

    const [persons, setPersons] = useState<PersonWithShifts[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);

    const [selectedShiftStatus, setSelectedShiftStatus] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [selectedFunctionId, setSelectedFunctionId] =
        useState<number | "">("");

    const [selectedPerson, setSelectedPerson] =
        useState<PersonWithShifts | null>(null);

    const [dragOverShiftId, setDragOverShiftId] =
        useState<number | null>(null);

    const [draggingPersonId, setDraggingPersonId] =
        useState<number | null>(null);

    const [showModal, setShowModal] = useState(false);

    // ---------------------------------------------------------
    // REPLACE PERSON
    // ---------------------------------------------------------

    const [replacePerson, setReplacePerson] =
        useState<PersonWithShifts | null>(null);

    const [allPersons, setAllPersons] = useState<any[]>([]);
    const [personSearch, setPersonSearch] = useState("");

    const [replacementPersonId, setReplacementPersonId] =
        useState<number | null>(null);

    const [replacingPerson, setReplacingPerson] = useState(false);

    // ---------------------------------------------------------
    // USER
    // ---------------------------------------------------------

    const personId = Number(
        sessionStorage.getItem("personId")
    );

    const roles: string[] = JSON.parse(
        sessionStorage.getItem("userRoles") || "[]"
    );

    const isAdmin = roles.includes("Admin");

    // ---------------------------------------------------------
    // HELPERS
    // ---------------------------------------------------------

    const matchesDate = (shift: Shift) => {
        if (!fromDate && !toDate) return true;

        const start = new Date(shift.start);

        if (fromDate && start < new Date(fromDate)) {
            return false;
        }

        if (
            toDate &&
            start > new Date(toDate + "T23:59:59")
        ) {
            return false;
        }

        return true;
    };

    const canAccessGroup = (group: VolunteerGroup) => {
        if (isAdmin) return true;

        return group.contactPersonId === personId;
    };

    const selectedGroup = selectedGroupId
        ? groups.find(g => g.id === selectedGroupId)
        : undefined;

    const canSelfAssign =
        selectedGroup?.selfAssignShifts ?? false;

    const formatShiftDate = (value: string) => {
        return new Date(value).toLocaleDateString(
            "da-DK",
            {
                day: "numeric",
                month: "short",
                year: "numeric",
            }
        );
    };

    const formatShiftTime = (value: string) => {
        return new Date(value).toLocaleTimeString(
            "da-DK",
            {
                hour: "2-digit",
                minute: "2-digit",
            }
        );
    };

    // ---------------------------------------------------------
    // LOAD GROUPS
    // ---------------------------------------------------------

    const loadGroups = async () => {
        try {
            const res = await fetch(
                `${apiUrl}/api/VolunteerGroups`,
                {
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                setGroups([]);
                setLoading(false);
                return;
            }

            const data = await res.json();

            const allGroups =
                data?.$values ?? data ?? [];

            const filteredByEvent =
                selectedEventId
                    ? allGroups.filter(
                        (g: VolunteerGroup) =>
                            g.eventId === selectedEventId
                    )
                    : allGroups;

            setGroups(filteredByEvent);
        } catch (error) {
            console.error(
                "Could not load groups:",
                error
            );

            setGroups([]);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------------
    // LOAD EVENTS
    // ---------------------------------------------------------

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/events`,
                    {
                        headers: getAuthHeaders(),
                    }
                );

                if (!res.ok) {
                    setEvents([]);
                    return;
                }

                const data = await res.json();

                const normalized = (
                    data?.$values ??
                    data ??
                    []
                ).map((e: any) => ({
                    eventId: e.eventID,
                    name: e.eventName,
                }));

                setEvents(normalized);
            } catch (error) {
                console.error(
                    "Could not load events:",
                    error
                );

                setEvents([]);
            }
        };

        loadEvents();
    }, [apiUrl]);

    // ---------------------------------------------------------
    // LOAD FUNCTIONS
    // ---------------------------------------------------------

    useEffect(() => {
        const loadFunctionsForEvent = async () => {
            if (!selectedEventId) {
                setFunctions([]);
                return;
            }

            try {
                const res = await fetch(
                    `${apiUrl}/api/events/${selectedEventId}/shifts/with-counts`,
                    {
                        headers: getAuthHeaders(),
                    }
                );

                if (!res.ok) {
                    setFunctions([]);
                    return;
                }

                const data = await res.json();

                const uniqueFunctions = Array.from(
                    new Map(
                        (
                            data?.$values ??
                            data ??
                            []
                        ).map((s: any) => [
                            s.functionID,
                            {
                                functionId:
                                    s.functionID,
                                functionName:
                                    s.functionName,
                                eventId:
                                    selectedEventId,
                            },
                        ])
                    ).values()
                );

                setFunctions(uniqueFunctions);
            } catch (error) {
                console.error(
                    "Could not load functions:",
                    error
                );

                setFunctions([]);
            }
        };

        loadFunctionsForEvent();
    }, [apiUrl, selectedEventId]);

    // ---------------------------------------------------------
    // LOAD GROUPS WHEN EVENT CHANGES
    // ---------------------------------------------------------

    useEffect(() => {
        loadGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);

    // ---------------------------------------------------------
    // LOAD SHIFTS
    // ---------------------------------------------------------

    const loadShifts = async (
        eventId: number,
        functionId?: number,
        from?: string,
        to?: string
    ) => {
        let url =
            `${apiUrl}/api/events/${eventId}/shifts/with-counts`;

        const params = new URLSearchParams();

        if (from) {
            params.append("fromDate", from);
        }

        if (to) {
            params.append("toDate", to);
        }

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        try {
            const res = await fetch(url, {
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                setShifts([]);
                return;
            }

            const data = await res.json();

            const mapped: Shift[] = (
                data?.$values ??
                data ??
                []
            ).map((s: any) => ({
                shiftId: s.shiftsID,
                functionId: s.functionID,
                functionName: s.functionName,
                title: s.shiftsName,
                start: s.startTime,
                end: s.endTime,
                maxParticipants:
                    s.maxParticipants,
                registeredCount:
                    s.registeredCount,
                waitlistAllowed:
                    s.waitlist,
            }));

            setShifts(mapped);
        } catch (error) {
            console.error(
                "Could not load shifts:",
                error
            );

            setShifts([]);
        }
    };

    useEffect(() => {
        if (!selectedEventId) {
            return;
        }

        loadShifts(
            selectedEventId,
            selectedFunctionId === ""
                ? undefined
                : selectedFunctionId,
            fromDate || undefined,
            toDate || undefined
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedEventId,
        selectedFunctionId,
        fromDate,
        toDate,
    ]);

    // ---------------------------------------------------------
    // LOAD PERSONS
    // ---------------------------------------------------------

    const loadPersonsForGroup =
        async (groupId: number) => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/VolunteerGroups/${groupId}/persons`,
                    {
                        headers: getAuthHeaders(),
                    }
                );

                if (!res.ok) {
                    setPersons([]);
                    return;
                }

                const data = await res.json();

                const personsNormalized:
                    PersonWithShifts[] = (
                        data?.$values ??
                        data ??
                        []
                    ).map((p: any) => ({
                        personId: p.personId,
                        userId: p.userId,
                        firstName: p.firstName,
                        lastName: p.lastName,
                        dateOfBirth: p.dateOfBirth,
                        country: p.country,
                        personImagePath:
                            p.personImagePath,
                        profileUrl: p.profileUrl,
                        hasProfileImage:
                            p.hasProfileImage,

                        events: p.events ?? [],
                        functions:
                            p.functions ?? [],

                        shifts: (
                            p.shifts ?? []
                        ).map((s: any) => ({
                            shiftId:
                                s.shiftId,
                            title: s.title,
                            functionName:
                                s.functionName,
                            start: s.start,
                            end: s.end,
                            status: s.status,
                        })),
                    }));

                setPersons(personsNormalized);
            } catch (error) {
                console.error(
                    "Could not load group persons:",
                    error
                );

                setPersons([]);
            }
        };

    useEffect(() => {
        if (selectedGroupId) {
            loadPersonsForGroup(
                selectedGroupId
            );
        } else {
            setPersons([]);
        }
    }, [selectedGroupId]);

    // ---------------------------------------------------------
    // LOAD ALL PERSONS
    // ---------------------------------------------------------

    useEffect(() => {
        const loadAllPersons = async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/VolunteerGroups/all-persons`,
                    {
                        headers:
                            getAuthHeaders(),
                    }
                );

                if (!res.ok) return;

                const data =
                    await res.json();

                setAllPersons(
                    data?.$values ??
                    data ??
                    []
                );
            } catch (error) {
                console.error(
                    "Could not load all persons:",
                    error
                );
            }
        };

        loadAllPersons();
    }, [apiUrl]);

    // ---------------------------------------------------------
    // SHIFT STATUS
    // ---------------------------------------------------------

    const getShiftStatus = (
        shift: Shift
    ) => {
        const max =
            shift.maxParticipants ?? 0;

        const count =
            shift.registeredCount ?? 0;

        if (max === 0) return "Åben";

        if (count < max) return "Ledig";

        return shift.waitlistAllowed
            ? "Venteliste"
            : "Lukket";
    };

    // ---------------------------------------------------------
    // FILTERED SHIFTS
    // ---------------------------------------------------------

    const openShifts = shifts
        .filter(matchesDate)
        .filter(s => {
            const matchFunction =
                selectedFunctionId === "" ||
                Number(s.functionId) ===
                Number(
                    selectedFunctionId
                );

            const matchStatus =
                selectedShiftStatus === "" ||
                getShiftStatus(s) ===
                selectedShiftStatus;

            return (
                matchFunction &&
                matchStatus
            );
        });

    const visibleGroups =
        groups.filter(canAccessGroup);

    // ---------------------------------------------------------
    // REMOVE SHIFT
    // ---------------------------------------------------------

    const removeShiftFromPerson =
        async (
            person: PersonWithShifts,
            shift: NonNullable<
                PersonWithShifts["shifts"]
            >[number]
        ) => {
            if (!selectedGroupId) return;

            const confirmed =
                window.confirm(
                    `Vil du fjerne vagten "${shift.title}" fra ${person.firstName} ${person.lastName}?`
                );

            if (!confirmed) return;

            const res = await fetch(
                `${apiUrl}/api/VolunteerGroups/${selectedGroupId}/person/${person.personId}/shift/${shift.shiftId}`,
                {
                    method: "DELETE",
                    headers:
                        getAuthHeaders(),
                }
            );

            if (!res.ok) {
                alert(
                    (await res.text()) ||
                    "Kunne ikke fjerne vagten."
                );

                return;
            }

            await loadPersonsForGroup(
                selectedGroupId
            );

            if (selectedEventId) {
                await loadShifts(
                    selectedEventId,
                    selectedFunctionId === ""
                        ? undefined
                        : selectedFunctionId,
                    fromDate || undefined,
                    toDate || undefined
                );
            }
        };

    // ---------------------------------------------------------
    // ASSIGN SHIFT
    // ---------------------------------------------------------

    const assignShift = async (
        shift: Shift,
        personId: number
    ) => {
        const res = await fetch(
            `${apiUrl}/api/shifts/${shift.shiftId}/register`,
            {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type":
                        "application/json",
                },
                body: JSON.stringify({
                    personId,
                    status: "Tilmeldt",
                }),
            }
        );

        if (!res.ok) {
            alert(
                (await res.text()) ||
                "Kunne ikke tildele vagten."
            );

            return;
        }

        if (selectedGroupId) {
            await loadPersonsForGroup(
                selectedGroupId
            );
        }

        if (selectedEventId) {
            await loadShifts(
                selectedEventId,
                selectedFunctionId === ""
                    ? undefined
                    : selectedFunctionId,
                fromDate || undefined,
                toDate || undefined
            );
        }
    };

    // ---------------------------------------------------------
    // LOADING
    // ---------------------------------------------------------

    if (loading) {
        return (
            <div className="vg-loading">
                Indlæser frivilliggrupper...
            </div>
        );
    }

    // =========================================================
    // SELECTED GROUP
    // =========================================================

    if (selectedGroup) {
        const eventName =
            events.find(
                e =>
                    e.eventId ===
                    selectedGroup.eventId
            )?.name ?? "";

        return (
            <div>
                <HeroSectionV2
                    titleEn="Volunteer Groups"
                    titleDa="Frivilliggrupper"
                    descriptionEn="Manage persons and shifts"
                    descriptionDa="Administrér personer og vagter"
                    imageSrc={Billede1}
                />

                <main className="vg-dashboard">
                    {/* BACK */}

                    <button
                        className="vg-back-button"
                        onClick={() =>
                            setSelectedGroupId(
                                null
                            )
                        }
                    >
                        ← Tilbage
                    </button>

                    {/* ====================================== */}
                    {/* PAGE HEADER */}
                    {/* ====================================== */}

                    <div className="vg-page-heading">
                        <div>
                            <div className="vg-eyebrow">
                                Frivilliggruppe
                            </div>

                            <h1>
                                {
                                    selectedGroup.name
                                }
                            </h1>

                            <div className="vg-page-meta">
                                <span>
                                    {
                                        selectedGroup.type
                                    }
                                </span>

                                <span className="vg-meta-dot">
                                    •
                                </span>

                                <span>
                                    {
                                        persons.length
                                    }{" "}
                                    personer
                                </span>
                            </div>
                        </div>

                        {canSelfAssign && (
                            <div className="vg-assignment-active">
                                <span className="vg-assignment-dot" />

                                Vagttildeling
                                aktiv
                            </div>
                        )}
                    </div>

                    {/* ====================================== */}
                    {/* FILTER PANEL */}
                    {/* ====================================== */}

                    {canSelfAssign && (
                        <section className="vg-filter-panel">
                            <div className="vg-filter-heading">
                                <div>
                                    <div className="vg-eyebrow">
                                        Event
                                    </div>

                                    <h2>
                                        {
                                            eventName
                                        }
                                    </h2>

                                    <p>
                                        Find de
                                        vagter du vil
                                        tildele
                                        gruppens
                                        personer.
                                    </p>
                                </div>

                                <div className="vg-shift-result">
                                    <strong>
                                        {
                                            openShifts.length
                                        }
                                    </strong>

                                    <span>
                                        vagter
                                    </span>
                                </div>
                            </div>

                            <div className="vg-filter-grid">
                                <div className="vg-filter-field">
                                    <label>
                                        Fra dato
                                    </label>

                                    <input
                                        type="date"
                                        value={
                                            fromDate
                                        }
                                        onChange={e =>
                                            setFromDate(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="vg-filter-field">
                                    <label>
                                        Til dato
                                    </label>

                                    <input
                                        type="date"
                                        value={
                                            toDate
                                        }
                                        onChange={e =>
                                            setToDate(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="vg-filter-field">
                                    <label>
                                        Funktion
                                    </label>

                                    <select
                                        value={
                                            selectedFunctionId
                                        }
                                        onChange={e =>
                                            setSelectedFunctionId(
                                                e
                                                    .target
                                                    .value
                                                    ? Number(
                                                        e
                                                            .target
                                                            .value
                                                    )
                                                    : ""
                                            )
                                        }
                                    >
                                        <option value="">
                                            Alle
                                            funktioner
                                        </option>

                                        {functions.map(
                                            (
                                                f: any
                                            ) => (
                                                <option
                                                    key={
                                                        f.functionId
                                                    }
                                                    value={
                                                        f.functionId
                                                    }
                                                >
                                                    {
                                                        f.functionName
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>

                                <div className="vg-filter-field">
                                    <label>
                                        Status
                                    </label>

                                    <select
                                        value={
                                            selectedShiftStatus
                                        }
                                        onChange={e =>
                                            setSelectedShiftStatus(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                    >
                                        <option value="">
                                            Alle
                                            statusser
                                        </option>

                                        <option value="Åben">
                                            Åben
                                        </option>

                                        <option value="Ledig">
                                            Ledig
                                        </option>

                                        <option value="Venteliste">
                                            Venteliste
                                        </option>

                                        <option value="Lukket">
                                            Lukket
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* ====================================== */}
                    {/* MAIN GRID */}
                    {/* ====================================== */}

                    <div
                        className={`vg-content-grid ${!canSelfAssign
                                ? "vg-content-grid-single"
                                : ""
                            }`}
                    >
                        {/* ================================== */}
                        {/* PERSONS */}
                        {/* ================================== */}

                        <section className="vg-column">
                            <div className="vg-section-heading">
                                <div>
                                    <h2>
                                        Personer i
                                        gruppen
                                    </h2>

                                    <p>
                                        Administrér
                                        gruppens
                                        personer og
                                        deres vagter.
                                    </p>
                                </div>

                                <span className="vg-count">
                                    {
                                        persons.length
                                    }
                                </span>
                            </div>

                            <div className="vg-person-list">
                                {persons.length ===
                                    0 ? (
                                    <div className="vg-empty">
                                        Ingen
                                        personer i
                                        gruppen.
                                    </div>
                                ) : (
                                    persons.map(
                                        p => {
                                            const assignedShifts =
                                                (
                                                    p.shifts ??
                                                    []
                                                ).filter(
                                                    s =>
                                                        (
                                                            s.status ??
                                                            ""
                                                        )
                                                            .trim()
                                                            .toLowerCase() ===
                                                        "tilmeldt"
                                                );

                                            return (
                                                <article
                                                    key={
                                                        p.personId
                                                    }
                                                    className={`vg-person-card ${draggingPersonId ===
                                                            p.personId
                                                            ? "is-dragging"
                                                            : ""
                                                        }`}
                                                    draggable={
                                                        canSelfAssign
                                                    }
                                                    onDragStart={e => {
                                                        if (
                                                            !canSelfAssign
                                                        ) {
                                                            e.preventDefault();
                                                            return;
                                                        }

                                                        setDraggingPersonId(
                                                            p.personId
                                                        );

                                                        e.dataTransfer.effectAllowed =
                                                            "move";

                                                        e.dataTransfer.setData(
                                                            "personId",
                                                            String(
                                                                p.personId
                                                            )
                                                        );
                                                    }}
                                                    onDragEnd={() =>
                                                        setDraggingPersonId(
                                                            null
                                                        )
                                                    }
                                                >
                                                    <div className="vg-person-top">
                                                        <div className="vg-person-identity">
                                                            <div className="vg-avatar">
                                                                {(
                                                                    p.firstName?.[0] ??
                                                                    ""
                                                                ).toUpperCase()}
                                                                {(
                                                                    p.lastName?.[0] ??
                                                                    ""
                                                                ).toUpperCase()}
                                                            </div>

                                                            <div className="vg-person-name">
                                                                <strong>
                                                                    {
                                                                        p.firstName
                                                                    }{" "}
                                                                    {
                                                                        p.lastName
                                                                    }
                                                                </strong>

                                                                <div
                                                                    className={`vg-profile-status ${p.hasProfileImage
                                                                            ? "is-ok"
                                                                            : "is-missing"
                                                                        }`}
                                                                >
                                                                    <span />

                                                                    {p.hasProfileImage
                                                                        ? "Profilbillede"
                                                                        : "Mangler billede"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="vg-person-actions">
                                                            <button
                                                                className="vg-button vg-button-light"
                                                                onClick={() => {
                                                                    setSelectedPerson(
                                                                        p
                                                                    );

                                                                    setShowModal(
                                                                        true
                                                                    );
                                                                }}
                                                            >
                                                                Rediger
                                                            </button>

                                                            <button
                                                                className="vg-button vg-button-light"
                                                                onClick={() => {
                                                                    setReplacePerson(
                                                                        p
                                                                    );

                                                                    setReplacementPersonId(
                                                                        null
                                                                    );

                                                                    setPersonSearch(
                                                                        ""
                                                                    );
                                                                }}
                                                            >
                                                                Udskift
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* SHIFTS */}
                                                    {canSelfAssign && (

                                                        <div className="vg-person-shifts-area">
                                                            <div className="vg-person-shifts-heading">
                                                                <span>
                                                                    Vagter
                                                                </span>

                                                                <span className="vg-mini-count">
                                                                    {
                                                                        assignedShifts.length
                                                                    }
                                                                </span>
                                                            </div>

                                                            {assignedShifts.length ===
                                                                0 ? (
                                                                <div className="vg-no-person-shifts">
                                                                    Ingen
                                                                    vagter
                                                                    tildelt
                                                                </div>
                                                            ) : (
                                                                <div className="vg-person-shifts-list">
                                                                    {assignedShifts.map(
                                                                        s => (
                                                                            <div
                                                                                key={
                                                                                    s.shiftId
                                                                                }
                                                                                className="vg-person-shift"
                                                                            >
                                                                                <div className="vg-person-shift-bar" />

                                                                                <div className="vg-person-shift-info">
                                                                                    <span className="vg-person-shift-function">
                                                                                        {
                                                                                            s.functionName
                                                                                        }
                                                                                    </span>

                                                                                    <strong>
                                                                                        {
                                                                                            s.title
                                                                                        }
                                                                                    </strong>

                                                                                    <span className="vg-person-shift-time">
                                                                                        {formatShiftDate(
                                                                                            s.start
                                                                                        )}

                                                                                        {" · "}

                                                                                        {formatShiftTime(
                                                                                            s.start
                                                                                        )}

                                                                                        {" – "}

                                                                                        {formatShiftTime(
                                                                                            s.end
                                                                                        )}
                                                                                    </span>
                                                                                </div>

                                                                                <button
                                                                                    type="button"
                                                                                    className="vg-remove-shift"
                                                                                    title="Fjern vagt"
                                                                                    onClick={e => {
                                                                                        e.stopPropagation();

                                                                                        removeShiftFromPerson(
                                                                                            p,
                                                                                            s
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {canSelfAssign && (
                                                        <div className="vg-drag-hint">
                                                            <span className="vg-drag-icon">
                                                                ⋮⋮
                                                            </span>

                                                            Træk
                                                            personen
                                                            over på
                                                            en vagt
                                                        </div>
                                                    )}
                                                </article>
                                            );
                                        }
                                    )
                                )}
                            </div>
                        </section>

                        {/* ================================== */}
                        {/* AVAILABLE SHIFTS */}
                        {/* ================================== */}

                        {canSelfAssign && (
                            <section className="vg-column vg-shifts-column">
                                <div className="vg-section-heading">
                                    <div>
                                        <h2>
                                            Tilgængelige
                                            vagter
                                        </h2>

                                        <p>
                                            Træk en
                                            person over
                                            på den
                                            ønskede
                                            vagt.
                                        </p>
                                    </div>

                                    <span className="vg-count">
                                        {
                                            openShifts.length
                                        }
                                    </span>
                                </div>

                                <div className="vg-shift-scroll">
                                    <div className="vg-shift-list">
                                        {openShifts.length ===
                                            0 ? (
                                            <div className="vg-empty">
                                                Ingen
                                                vagter
                                                matcher
                                                filtrene.
                                            </div>
                                        ) : (
                                            openShifts.map(
                                                shift => {
                                                    const status =
                                                        getShiftStatus(
                                                            shift
                                                        );

                                                    return (
                                                        <article
                                                            key={
                                                                shift.shiftId
                                                            }
                                                            className={`vg-shift-card ${dragOverShiftId ===
                                                                    shift.shiftId
                                                                    ? "is-drop-target"
                                                                    : ""
                                                                }`}
                                                            onDragOver={e => {
                                                                e.preventDefault();

                                                                e.dataTransfer.dropEffect =
                                                                    "move";

                                                                setDragOverShiftId(
                                                                    shift.shiftId
                                                                );
                                                            }}
                                                            onDragLeave={() =>
                                                                setDragOverShiftId(
                                                                    null
                                                                )
                                                            }
                                                            onDrop={async e => {
                                                                e.preventDefault();

                                                                setDragOverShiftId(
                                                                    null
                                                                );

                                                                const draggedPersonId =
                                                                    Number(
                                                                        e.dataTransfer.getData(
                                                                            "personId"
                                                                        )
                                                                    );

                                                                if (
                                                                    !draggedPersonId
                                                                ) {
                                                                    return;
                                                                }

                                                                await assignShift(
                                                                    shift,
                                                                    draggedPersonId
                                                                );
                                                            }}
                                                        >
                                                            <div className="vg-shift-top">
                                                                <div>
                                                                    <span className="vg-shift-function">
                                                                        {
                                                                            shift.functionName
                                                                        }
                                                                    </span>

                                                                    <h3>
                                                                        {
                                                                            shift.title
                                                                        }
                                                                    </h3>
                                                                </div>

                                                                <span
                                                                    className={`vg-status vg-status-${status.toLowerCase()}`}
                                                                >
                                                                    {
                                                                        status
                                                                    }
                                                                </span>
                                                            </div>

                                                            <div className="vg-shift-date">
                                                                {formatShiftDate(
                                                                    shift.start
                                                                )}
                                                            </div>

                                                            <div className="vg-shift-time">
                                                                <span>
                                                                    {formatShiftTime(
                                                                        shift.start
                                                                    )}
                                                                </span>

                                                                <span className="vg-time-arrow">
                                                                    →
                                                                </span>

                                                                <span>
                                                                    {formatShiftTime(
                                                                        shift.end
                                                                    )}
                                                                </span>
                                                            </div>

                                                            <div className="vg-shift-footer">
                                                                <span>
                                                                    {
                                                                        shift.registeredCount ??
                                                                        0
                                                                    }

                                                                    {shift.maxParticipants
                                                                        ? ` / ${shift.maxParticipants}`
                                                                        : ""}{" "}
                                                                    tilmeldt
                                                                </span>

                                                                <span className="vg-drop-text">
                                                                    Slip
                                                                    person
                                                                    her
                                                                </span>
                                                            </div>
                                                        </article>
                                                    );
                                                }
                                            )
                                        )}
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>
                </main>

                {/* ========================================== */}
                {/* EDIT PERSON */}
                {/* ========================================== */}

                {showModal &&
                    selectedPerson && (
                        <PersonSimpleModalForm
                            initialPerson={
                                selectedPerson
                            }
                            currentUserId={
                                personId
                            }
                            onCancel={() => {
                                setShowModal(false);
                                setSelectedPerson(
                                    null
                                );
                            }}
                            onSave={async p => {
                                setShowModal(false);

                                const oldPerson =
                                    selectedPerson;

                                setSelectedPerson(
                                    null
                                );

                                const payload = {
                                    personId:
                                        p.personId,
                                    firstName:
                                        p.firstName,
                                    lastName:
                                        p.lastName,
                                    dateOfBirth:
                                        p.dateOfBirth,
                                    country:
                                        p.country,
                                    personImagePath:
                                        p.personImagePath,
                                    uploadImageBase64:
                                        p.uploadImageBase64,

                                    events:
                                        p.events ??
                                        oldPerson?.events ??
                                        [],

                                    functions:
                                        p.functions ??
                                        oldPerson?.functions ??
                                        [],
                                };

                                await fetch(
                                    `${apiUrl}/api/VolunteerGroups/person/${p.personId}`,
                                    {
                                        method:
                                            "PUT",
                                        headers: {
                                            ...getAuthHeaders(),
                                            "Content-Type":
                                                "application/json",
                                        },
                                        body: JSON.stringify(
                                            payload
                                        ),
                                    }
                                );

                                if (
                                    selectedGroupId
                                ) {
                                    await loadPersonsForGroup(
                                        selectedGroupId
                                    );
                                }
                            }}
                        />
                    )}

                {/* ========================================== */}
                {/* REPLACE PERSON */}
                {/* ========================================== */}

                {replacePerson && (
                    <div className="vg-modal-overlay">
                        <div className="vg-replace-modal">
                            <div className="vg-modal-header">
                                <div>
                                    <span className="vg-eyebrow">
                                        Frivilliggruppe
                                    </span>

                                    <h2>
                                        Udskift
                                        person
                                    </h2>

                                    <p>
                                        Udskift{" "}
                                        <strong>
                                            {
                                                replacePerson.firstName
                                            }{" "}
                                            {
                                                replacePerson.lastName
                                            }
                                        </strong>{" "}
                                        med en
                                        anden
                                        person.
                                    </p>
                                </div>

                                <button
                                    className="vg-modal-close"
                                    onClick={() => {
                                        setReplacePerson(
                                            null
                                        );

                                        setReplacementPersonId(
                                            null
                                        );

                                        setPersonSearch(
                                            ""
                                        );
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="vg-replace-search">
                                <input
                                    type="text"
                                    placeholder="Søg efter navn eller virksomhed..."
                                    value={
                                        personSearch
                                    }
                                    onChange={e =>
                                        setPersonSearch(
                                            e
                                                .target
                                                .value
                                        )
                                    }
                                    autoFocus
                                />
                            </div>

                            <div className="vg-replace-list">
                                {allPersons
                                    .filter(p => {
                                        if (
                                            p.personId ===
                                            replacePerson.personId
                                        ) {
                                            return false;
                                        }

                                        if (
                                            persons.some(
                                                existing =>
                                                    existing.personId ===
                                                    p.personId
                                            )
                                        ) {
                                            return false;
                                        }

                                        const search =
                                            personSearch
                                                .trim()
                                                .toLowerCase();

                                        if (!search) {
                                            return true;
                                        }

                                        const searchable =
                                            `${p.firstName ?? ""} ${p.lastName ?? ""} ${p.company ?? ""}`
                                                .toLowerCase();

                                        return searchable.includes(
                                            search
                                        );
                                    })
                                    .slice(0, 30)
                                    .map(p => (
                                        <button
                                            type="button"
                                            key={
                                                p.personId
                                            }
                                            className={`vg-replace-person ${replacementPersonId ===
                                                    p.personId
                                                    ? "is-selected"
                                                    : ""
                                                }`}
                                            onClick={() =>
                                                setReplacementPersonId(
                                                    p.personId
                                                )
                                            }
                                        >
                                            <div className="vg-replace-avatar">
                                                {(
                                                    p.firstName?.[0] ??
                                                    ""
                                                ).toUpperCase()}
                                                {(
                                                    p.lastName?.[0] ??
                                                    ""
                                                ).toUpperCase()}
                                            </div>

                                            <div>
                                                <strong>
                                                    {
                                                        p.firstName
                                                    }{" "}
                                                    {
                                                        p.lastName
                                                    }
                                                </strong>

                                                {p.company && (
                                                    <span>
                                                        {
                                                            p.company
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                            </div>

                            <div className="vg-modal-footer">
                                <button
                                    className="vg-button vg-button-light"
                                    onClick={() => {
                                        setReplacePerson(
                                            null
                                        );

                                        setReplacementPersonId(
                                            null
                                        );

                                        setPersonSearch(
                                            ""
                                        );
                                    }}
                                >
                                    Annuller
                                </button>

                                <button
                                    className="vg-button vg-button-primary"
                                    disabled={
                                        !replacementPersonId ||
                                        replacingPerson
                                    }
                                    onClick={async () => {
                                        if (
                                            !selectedGroupId ||
                                            !replacementPersonId
                                        ) {
                                            return;
                                        }

                                        const confirmed =
                                            window.confirm(
                                                `Er du sikker på, at du vil udskifte ${replacePerson.firstName} ${replacePerson.lastName}?`
                                            );

                                        if (
                                            !confirmed
                                        ) {
                                            return;
                                        }

                                        setReplacingPerson(
                                            true
                                        );

                                        try {
                                            const res =
                                                await fetch(
                                                    `${apiUrl}/api/VolunteerGroups/${selectedGroupId}/replace-person`,
                                                    {
                                                        method:
                                                            "POST",
                                                        headers:
                                                        {
                                                            ...getAuthHeaders(),
                                                            "Content-Type":
                                                                "application/json",
                                                        },
                                                        body: JSON.stringify(
                                                            {
                                                                oldPersonId:
                                                                    replacePerson.personId,
                                                                newPersonId:
                                                                    replacementPersonId,
                                                            }
                                                        ),
                                                    }
                                                );

                                            if (
                                                !res.ok
                                            ) {
                                                alert(
                                                    (await res.text()) ||
                                                    "Kunne ikke udskifte personen."
                                                );

                                                return;
                                            }

                                            setReplacePerson(
                                                null
                                            );

                                            setReplacementPersonId(
                                                null
                                            );

                                            setPersonSearch(
                                                ""
                                            );

                                            await loadPersonsForGroup(
                                                selectedGroupId
                                            );

                                            if (
                                                selectedEventId
                                            ) {
                                                await loadShifts(
                                                    selectedEventId,
                                                    selectedFunctionId ===
                                                        ""
                                                        ? undefined
                                                        : selectedFunctionId,
                                                    fromDate ||
                                                    undefined,
                                                    toDate ||
                                                    undefined
                                                );
                                            }
                                        } catch (
                                        error
                                        ) {
                                            console.error(
                                                "Replace person failed:",
                                                error
                                            );

                                            alert(
                                                "Der opstod en fejl ved udskiftning af personen."
                                            );
                                        } finally {
                                            setReplacingPerson(
                                                false
                                            );
                                        }
                                    }}
                                >
                                    {replacingPerson
                                        ? "Udskifter..."
                                        : "Udskift person"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // =========================================================
    // GROUP LIST
    // =========================================================

    return (
        <div>
            <HeroSectionV2
                titleEn="Volunteer Groups"
                titleDa="Frivilliggrupper"
                descriptionEn="Volunteer groups"
                descriptionDa="Administrér dine frivilliggrupper"
                imageSrc={Billede1}
            />

            <main className="vg-dashboard">
                <div className="vg-page-heading">
                    <div>
                        <div className="vg-eyebrow">
                            Administration
                        </div>

                        <h1>
                            Frivilliggrupper
                        </h1>

                        <div className="vg-page-meta">
                            Vælg en gruppe for
                            at administrere
                            personer og vagter.
                        </div>
                    </div>
                </div>

                <div className="vg-group-select-section">
    <div className="vg-group-select-heading">
        <div>
            <span className="vg-eyebrow">Vælg gruppe</span>
            <h2>Hvilken frivilliggruppe vil du administrere?</h2>
            <p>
                Vælg en gruppe for at se personer, vagter og indstillinger.
            </p>
        </div>

        <span className="vg-group-total">
            {visibleGroups.length} grupper
        </span>
    </div>

    <div className="vg-group-grid">
        {visibleGroups.map(g => {
            const groupEvent = events.find(
                e => e.eventId === g.eventId
            );

            return (
                <button
                    type="button"
                    key={g.id}
                    className="vg-group-card"
                    onClick={() => setSelectedGroupId(g.id!)}
                >
                    <div className="vg-group-card-icon">
                        {g.name.substring(0, 2).toUpperCase()}
                    </div>

                    <div className="vg-group-card-content">
                        <span className="vg-group-type">
                            {g.type}
                        </span>

                        <h3>{g.name}</h3>

                        <span className="vg-group-event">
                            {groupEvent?.name ?? "Event"}
                        </span>
                    </div>

                    <div className="vg-group-card-arrow">
                        →
                    </div>
                </button>
            );
        })}
    </div>
</div>
            </main>
        </div>
    );
};

export default VolunteerGroupsDashboard;